import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, formatIDR, publicFileUrl } from "../lib/api";
import { BRAND_META } from "../context/BrandContext";
import { formatProductName } from "../lib/productName";
import { shopeeUrl, ShopeeLogo } from "../lib/marketplace";
import Logo from "../components/Logo";
import { MagnifyingGlass } from "@phosphor-icons/react";

export default function PublicCatalog({ brand }) {
    const meta = BRAND_META[brand] || { name: brand, accent: "#6F7D69", bg: "#F8F6F2" };
    const isHeelsAndTea = brand === "heelsandtea";

    // Mirror the catalog's brand onto <html> so the global CSS variables
    // (sage/dusty-rose palette) match the content being viewed — even when
    // the viewer isn't signed in.
    useEffect(() => {
        const prev = document.documentElement.getAttribute("data-brand");
        document.documentElement.setAttribute("data-brand", brand || "hoshizora");
        return () => {
            if (prev) document.documentElement.setAttribute("data-brand", prev);
            else document.documentElement.removeAttribute("data-brand");
        };
    }, [brand]);

    const [products, setProducts] = useState([]);
    const [cats, setCats] = useState([]);          // Hoshizora path uses this
    const [groups, setGroups] = useState([]);      // Heels & Tea Level 1
    const [featured, setFeatured] = useState([]);  // Heels & Tea Japan sub-brands
    const [q, setQ] = useState("");
    const [level1, setLevel1] = useState("all");   // slug / "all"
    const [level2, setLevel2] = useState("all");   // category name OR featured product_brand OR "all"
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                if (isHeelsAndTea) {
                    const [sr, pr] = await Promise.all([
                        axios.get(`${API}/public/catalog/structure`, { params: { brand } }),
                        axios.get(`${API}/public/products`, { params: { brand } }),
                    ]);
                    setGroups(sr.data.groups || []);
                    setCats(sr.data.categories || []);
                    setFeatured(sr.data.featured || []);
                    setProducts(pr.data || []);
                } else {
                    const [pr, cr] = await Promise.all([
                        axios.get(`${API}/public/products`, { params: { brand } }),
                        axios.get(`${API}/public/categories`, { params: { brand } }),
                    ]);
                    setProducts(pr.data || []);
                    setCats(cr.data || []);
                }
            } catch {
                setProducts([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [brand, isHeelsAndTea]);

    // Determine Level 2 chip set for Heels & Tea
    const activeLevel1Group = groups.find((g) => g.slug === level1);
    const japanSelected = level1 === "japan";
    const level2Items = useMemo(() => {
        if (!isHeelsAndTea) return [];
        const base = cats.map((c) => ({ kind: "category", value: c.name, label: c.name }));
        if (japanSelected) {
            return [
                ...base,
                ...featured.filter((f) => f.region_slug === "japan").map((f) => ({
                    kind: "brand", value: f.product_brand, label: f.display_name, isBrand: true,
                })),
            ];
        }
        return base;
    }, [isHeelsAndTea, cats, featured, japanSelected]);

    // Compute visible products with filters
    const filtered = useMemo(() => {
        const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
        return products.filter((p) => {
            if (isHeelsAndTea) {
                // Level 1
                if (level1 !== "all" && activeLevel1Group) {
                    if (activeLevel1Group.kind === "region" && p.region !== level1) return false;
                    if (activeLevel1Group.kind === "collection" && !(p.collections || []).includes(level1)) return false;
                }
                // Level 2
                if (level2 !== "all") {
                    const item = level2Items.find((i) => i.value === level2);
                    if (item?.kind === "brand") {
                        if ((p.product_brand || "") !== item.value) return false;
                    } else {
                        if (p.category !== level2) return false;
                    }
                }
            } else {
                if (level2 !== "all" && p.category !== level2) return false;
            }
            if (tokens.length) {
                const hay = [p.name, p.name_jp, p.category, p.carat, p.collection, p.product_brand, p.region]
                    .filter(Boolean).join(" ").toLowerCase();
                if (!tokens.every((t) => hay.includes(t))) return false;
            }
            return true;
        });
    }, [products, q, level1, level2, isHeelsAndTea, activeLevel1Group, level2Items]);

    const regionLabel = (slug) => groups.find((g) => g.slug === slug && g.kind === "region");

    return (
        <div className="min-h-screen w-full" style={{ background: meta.bg }}>
            <header className="border-b border-black/5 bg-white/50 backdrop-blur">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-6 flex items-center justify-center md:justify-start">
                    <Link to={`/${brand}`} aria-label={meta.name} data-testid={`public-brand-logo-${brand}`} className="inline-flex items-center">
                        {/* Mobile 36px → Tablet 48px → Desktop 60px. Same asset at every breakpoint. */}
                        <span className="block md:hidden"><Logo brand={brand} height={36} /></span>
                        <span className="hidden md:block lg:hidden"><Logo brand={brand} height={48} /></span>
                        <span className="hidden lg:block"><Logo brand={brand} height={60} /></span>
                    </Link>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
                <div className="relative mb-5">
                    <MagnifyingGlass size={22} weight="light" className="absolute left-0 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search products…"
                        data-testid="public-search-input"
                        className="w-full pl-8 pr-4 py-3 border-b border-black/10 focus:border-black/40 outline-none font-serif text-lg md:text-2xl bg-transparent transition-colors"
                    />
                </div>

                {isHeelsAndTea ? (
                    <>
                        {/* Level 1 — Region / Collection */}
                        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-3 -mx-1 px-1 scrollbar-none" data-testid="public-level1">
                            <Chip active={level1 === "all"} onClick={() => { setLevel1("all"); setLevel2("all"); }} accent={meta.accent} testId="public-l1-all">
                                All
                            </Chip>
                            {groups.map((g) => (
                                <Chip key={g.id} active={level1 === g.slug} onClick={() => { setLevel1(g.slug); setLevel2("all"); }} accent={meta.accent} testId={`public-l1-${g.slug}`}>
                                    <span className="mr-1">{g.icon}</span>{g.name}
                                </Chip>
                            ))}
                        </div>
                        {/* Level 2 — Categories (+ brand filters under Japan) */}
                        <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 -mx-1 px-1 scrollbar-none" data-testid="public-level2">
                            <Chip active={level2 === "all"} onClick={() => setLevel2("all")} accent={meta.accent} testId="public-l2-all">
                                All
                            </Chip>
                            {level2Items.map((it) => (
                                <Chip key={`${it.kind}-${it.value}`} active={level2 === it.value} onClick={() => setLevel2(level2 === it.value ? "all" : it.value)} accent={meta.accent} testId={`public-l2-${it.value}`}>
                                    {it.label}
                                </Chip>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-6 -mx-1 px-1 scrollbar-none">
                        <Chip active={level2 === "all"} onClick={() => setLevel2("all")} accent={meta.accent} testId="public-cat-all">All</Chip>
                        {cats.map((c) => (
                            <Chip key={c.id} active={level2 === c.name} onClick={() => setLevel2(level2 === c.name ? "all" : c.name)} accent={meta.accent} testId={`public-cat-${c.name}`}>
                                {c.name}
                            </Chip>
                        ))}
                    </div>
                )}

                {loading ? (
                    <div className="text-sm text-neutral-400 py-24 text-center">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="border border-black/10 bg-white/60 rounded-sm p-10 md:p-16 text-center">
                        <p className="font-serif text-2xl">No products match these filters.</p>
                        <p className="text-sm text-neutral-500 mt-1">Try a different combination.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6" data-testid="public-grid">
                        {filtered.map((p) => (
                            <PublicCard key={p.id} brand={brand} product={p} accent={meta.accent} regionBadge={regionLabel(p.region)} />
                        ))}
                    </div>
                )}
            </main>

            <footer className="border-t border-black/5 bg-white/40 mt-10">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 text-xs text-neutral-500 flex items-center justify-between">
                    <span>© {new Date().getFullYear()} {meta.name}</span>
                    <Link to="/" className="hover:text-neutral-700">Choose catalog</Link>
                </div>
            </footer>
        </div>
    );
}

function Chip({ children, active, onClick, accent, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className="shrink-0 px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border transition-colors"
            style={active ? { background: accent, color: "#fff", borderColor: accent } : { borderColor: "rgba(0,0,0,0.12)", color: "#4b4b45" }}
        >
            {children}
        </button>
    );
}

function PublicCard({ brand, product, accent, regionBadge }) {
    const main = product.images?.find((i) => i.is_main) || product.images?.[0];
    const prices = (product.variants || []).map((v) => Number(v.retail_price) || 0).filter(Boolean);
    const minP = prices.length ? Math.min(...prices) : product.retail_price;
    const maxP = prices.length ? Math.max(...prices) : product.retail_price;
    const priceLabel = prices.length && minP !== maxP ? `${formatIDR(minP)} – ${formatIDR(maxP)}` : formatIDR(product.retail_price || minP);
    const shopee = shopeeUrl(product.shopee_url);

    return (
        <Link to={`/${brand}/${product.id}`} data-testid={`public-card-${product.id}`} className="group flex flex-col border border-black/10 bg-white rounded-sm overflow-hidden hover:border-black/40 transition-colors">
            <div className="aspect-square bg-black/[0.03] relative flex items-center justify-center p-3">
                {main ? (
                    <img src={publicFileUrl(main.path)} alt={product.name} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                ) : (
                    <div className="text-xs uppercase tracking-widest text-neutral-300">No photo</div>
                )}
                {regionBadge && (
                    <span
                        className="absolute top-2 left-2 inline-flex items-center gap-1 bg-white/90 backdrop-blur rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest border border-black/5 shadow-sm"
                        data-testid={`public-card-region-${regionBadge.slug}`}
                    >
                        <span>{regionBadge.icon}</span>
                        <span className="text-neutral-700">{regionBadge.name}</span>
                    </span>
                )}
            </div>
            <div className="p-4 space-y-1.5 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{product.category}</div>
                <h3 className="font-sans font-medium text-[15px] md:text-[17px] leading-[1.3] min-h-[2.6em] line-clamp-2" data-testid={`public-card-title-${product.id}`}>{formatProductName(product.name)}</h3>
                {product.sku && <div className="font-mono text-xs text-neutral-600 truncate" data-testid={`public-card-sku-${product.id}`}>{product.sku}</div>}
                {brand === "hoshizora" && product.carat && <div className="text-xs text-neutral-500 font-sans" data-testid={`public-card-carat-${product.id}`}>{product.carat}</div>}
                <div className="!mt-auto pt-2 border-t border-black/5 font-sans flex items-end justify-between gap-2">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Price</div>
                        <div className="text-sm md:text-base font-medium tabular-nums" style={{ color: accent }}>{priceLabel}</div>
                    </div>
                    {shopee && (
                        <button type="button" aria-label="Shop on Shopee" title="Shop on Shopee"
                            data-testid={`public-card-shopee-${product.id}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(shopee, "_blank", "noopener,noreferrer"); }}
                            className="shrink-0 p-0.5 hover:opacity-80 transition-opacity">
                            <ShopeeLogo size={18} />
                        </button>
                    )}
                </div>
            </div>
        </Link>
    );
}
